import { useEffect, useRef, useState } from "react"
import { colors } from "../../../ui/colors"
import { CodeIcon } from "../../../ui/icons/code"
import { XCircleIcon } from "../../../ui/icons/xCircle"
import Input from "../../../ui/molecules/input"
import { SelectCustom } from "../../../ui/molecules/select"
import { AccordionFilter } from "../../../ui/organisms/accordionFilter"
import { useAppSelector } from "../../redux/hooks"
import { FindAllContactsFilters } from "../dto"

interface Props {
    updateFilters: (filter: Partial<FindAllContactsFilters>) => void
    filters: FindAllContactsFilters
    atecoSign?: string
    setAtecoSign: (ateco?: string) => void
    inputs: { first: string, second: string }[]
    setInputs: (inputs: { first: string, second: string }[]) => void
    clearAll: boolean
}

export function AtecoFilter(props: Props) {
    const { updateFilters, filters, atecoSign, setAtecoSign, inputs, setInputs } = props
    const atecosState = useAppSelector(state => state.atecos)

    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [emptyAtecos, setEmptyAtecos] = useState<{ label: string, value: string }[] | undefined>(undefined)
    const [suggestions, setSuggestions] = useState<{ label: string, value: string }[]>([]);
    const [suggestionsSecondInput, setSuggestionsSecondInput] = useState<{ label: string, value: string }[]>([]);

    const suggestionRef = useRef<HTMLUListElement>(null);
    const suggestionSecondRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                suggestionRef.current &&
                !suggestionRef.current.contains(event.target as Node)
            ) {
                setSuggestions([]);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                suggestionSecondRef.current &&
                !suggestionSecondRef.current.contains(event.target as Node)
            ) {
                setSuggestionsSecondInput([]);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const signOptions = [
        { label: "Uguale a", value: "=" },
        { label: "Inizia per", value: "~" },
        { label: "Compreso tra", value: "><" },
    ];

    const defaultAtecos = atecosState.findAllAtecosResponse.map((sector) => ({ label: sector.atecoCode + ' - ' + sector.atecoDescription, value: sector.atecoCode }))

    const handleClear = () => {
        setInputs([{ first: "", second: "" }]);
        setActiveIndex(null);
        setAtecoSign(undefined);
        setEmptyAtecos(undefined);
        setSuggestions([]);
        setSuggestionsSecondInput([])
        updateFilters({ companyAtecoCode: undefined });
    };

    useEffect(() => {
        if (props.clearAll) {
            handleClear();
        }
    }, [props.clearAll]);

    const handleFirstInputChange = (index: number, value: string) => {
        const newInputs = [...inputs];
        newInputs[index] = { first: value, second: inputs[index].second };
        setInputs([...newInputs]);
        setSuggestions(
            value.length > 0
                ? defaultAtecos.filter(ateco => ateco.label.toLowerCase().startsWith(value.toLowerCase()))
                : []
        );
        setActiveIndex(index);
    };

    const handleSecondInputChange = (index: number, value: string) => {
        const newInputs = [...inputs];
        newInputs[index] = { first: inputs[index].first, second: value };
        setInputs([...newInputs]);
        setSuggestionsSecondInput(
            value.length > 0
                ? defaultAtecos.filter(ateco => ateco.label.toLowerCase().startsWith(value.toLowerCase()))
                : []
        );
        setActiveIndex(index);
    };

    const handleSelectSuggestion = (index: number, value: string, input: 1 | 2) => {
        const newInputs = [...inputs];
        if (input === 1) {
            newInputs[index] = { first: value, second: inputs[index].second ?? "" };
        } else {
            newInputs[index] = { first: inputs[index] && inputs[index].first ? inputs[index].first : "", second: value };
        }
        setInputs([...newInputs]);
        setActiveIndex(null);
        setSuggestions([]);
        setSuggestionsSecondInput([]);
    };

    useEffect(() => {
        if (atecoSign === undefined && filters.companyAtecoCode === undefined) {
            return;
        }

        if (inputs.length === 0 && atecoSign === undefined) {
            setEmptyAtecos(undefined);
            setAtecoSign(undefined)
            updateFilters({ companyAtecoCode: undefined });
        } else if (atecoSign === "=" && inputs.length > 0 && inputs.some(input => input.first !== "")) {
            let values = inputs.map(input => ({ label: signOptions.find(opt => opt.value === atecoSign)?.label + " " + (input.first), value: atecoSign + ":" + input.first }));
            updateFilters({ companyAtecoCode: values });
        } else if (atecoSign === "~" && inputs.length > 0 && inputs.some(input => input.first !== "")) {
            let values = inputs.map(input => ({ label: signOptions.find(opt => opt.value === atecoSign)?.label + " " + (input.first), value: atecoSign + ":" + input.first }));
            updateFilters({ companyAtecoCode: values });
        } else if (atecoSign === "><" && inputs.length > 0 && inputs.some(input => input && input.first !== "" && input.second !== "")) {
            let values = inputs.map((input, index) => ({ label: signOptions.find(opt => opt.value === atecoSign)?.label + " " + input.first + " e " + input.second, value: atecoSign + ":" + input.first + ":" + input.second }));
            updateFilters({ companyAtecoCode: values });
        } else if (inputs.length === 1 && inputs[0].first === '' && inputs[0].second === '' && atecoSign !== undefined) {
            updateFilters({ companyAtecoCode: undefined });
            const label = signOptions.find(opt => opt.value === atecoSign)?.label;
            setEmptyAtecos([{ label: label!, value: label! }]);
        }
    }, [inputs, atecoSign]);

    useEffect(() => {
        if (inputs.length === 0 || atecoSign !== "=") {
            if (filters.companyCategory.length === 0 && filters.companySector.length === 0) {
                return;
            }
            updateFilters({ companyCategory: [], companySector: [] });
        } else if (filters.companyAtecoCode?.length !== 0 && atecoSign === "=") {
            const atecoCodes = filters.companyAtecoCode?.map((value) => value.value.slice(2)) || [];
            let filterSector: { label: string, value: string }[] = []
            let filterCategory: { label: string, value: string }[] = []

            atecoCodes.forEach(atecoCode => {
                const categoryAndSector = atecosState.findAllAtecosResponse.find(_ateco => _ateco.atecoCode === atecoCode);

                const category = categoryAndSector?.category;
                const sector = categoryAndSector?.sector;

                if (category && !filterCategory.map(cat => cat.value).includes(category)) {
                    filterCategory.push({ value: category, label: category });
                }

                if (sector && !filterSector.map(sector => sector.value).includes(sector)) {
                    filterSector.push({ value: sector, label: sector });
                }
            });

            updateFilters({ companyCategory: filterCategory, companySector: filterSector });
        }
    }, [filters.companyAtecoCode, atecoSign]);

    return (
        <AccordionFilter
            key={'person-filter-ateco'}
            label={"Codice Ateco"}
            icon={<CodeIcon color={""} size={0} />}
            showClear={filters.companyAtecoCode !== undefined || atecoSign !== undefined}
            options={filters.companyAtecoCode !== undefined ? filters.companyAtecoCode : emptyAtecos !== undefined ? emptyAtecos : undefined}
            clearAction={handleClear}
            deleteFilter={handleClear}
        >
            <div className="flex flex-col gap-4">
                <SelectCustom
                    isSearchable={false}
                    onChange={(e) => {
                        if (e !== undefined && typeof e === 'string') {
                            setAtecoSign(e as '=' | '~' | '><' | undefined)
                        }
                    }}
                    defaultValue={atecoSign}
                    placeholder={"Uguale a, inizia per, compreso"}
                    options={signOptions}
                />
                {atecoSign !== undefined && (
                    <div className="flex gap-2">
                        <div className="flex flex-col gap-1 w-full">
                            {
                                inputs.map((input, index) => (
                                    <div className="flex flex-row items-center gap-2">
                                        <div key={index} className="relative w-full">
                                            <Input
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        if (activeIndex !== null) {
                                                            handleSelectSuggestion(activeIndex, input.first, 1);
                                                        }
                                                    }
                                                }}
                                                key={"people-filter-ateco-input-" + index}
                                                value={input && input.first ? input.first : ""}
                                                onChangeText={(e) => handleFirstInputChange(index, e)}
                                                placeholder={signOptions.find(opt => opt.value === atecoSign)?.label}
                                            />
                                            {activeIndex === index && suggestions.length > 0 && (
                                                <ul ref={suggestionRef} className="absolute top-full left-0 w-full bg-white border border-neutral-300 rounded-md max-h-48 overflow-auto z-50">
                                                    {suggestions.map((sugg, idx) => (
                                                        <li
                                                            key={idx}
                                                            className="p-2 hover:bg-brandPrimary-200 cursor-pointer text-text-sm font-normal text-neutral-600"
                                                            onClick={() => handleSelectSuggestion(index, sugg.value, 1)}
                                                        >
                                                            {sugg.label}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                        {
                                            atecoSign === '><' &&
                                            <div className="w-full relative">
                                                <Input
                                                    key={"people-filter-ateco-input-2-" + index}
                                                    value={input && input.second ? input.second : ""}
                                                    defaultValue={input && input.second ? input.second : ""}
                                                    onChangeText={(e) => handleSecondInputChange(index, e)}
                                                    placeholder={"Compreso tra"}
                                                />
                                                {activeIndex === index && suggestionsSecondInput.length > 0 && (
                                                    <ul ref={suggestionSecondRef} className="absolute top-full left-0 w-full bg-white border border-neutral-300 rounded-md max-h-48 overflow-auto z-50">
                                                        {suggestionsSecondInput.map((sugg, _index) => (
                                                            <li
                                                                key={_index}
                                                                className="p-2 hover:bg-brandPrimary-200 cursor-pointer text-text-sm font-normal text-neutral-600"
                                                                onClick={() => handleSelectSuggestion(index, sugg.value, 2)}
                                                            >
                                                                {sugg.label}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        }
                                        <div>
                                            <XCircleIcon
                                                color={colors.neutral[500]}
                                                size={20}
                                                onClick={() => {
                                                    if (inputs.length === 1) {
                                                        setInputs([{ first: "", second: "" }]);
                                                    }
                                                    else {
                                                        setInputs(inputs.filter((_, i) => i !== index))
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                ))
                            }
                            <span onClick={() => {
                                setInputs([...inputs, { first: "", second: "" }])
                            }} className="flex justify-end text-text-xs text-brandPrimary-600 font-medium mt-1 cursor-pointer">
                                Aggiungi campo
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </AccordionFilter>
    )
}